import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BIRDS } from "@/lib/brand";
import { useLang } from "@/lib/i18n";

const TABS = [
  {
    key: "stationery",
    label: "Corporate Stationery",
    subtitle: "Everyday, executive.",
    tags: [
      "Business Cards",
      "Letterheads",
      "Envelopes",
      "ID Cards",
      "Presentation Folders",
      "Executive Journals",
      "Wiro Notebooks",
      "Sticky Notes",
    ],
    images: [
      "https://images.unsplash.com/photo-1782857690704-c4341b240487?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwYnVzaW5lc3MlMjBjYXJkJTIwZWxlZ2FudCUyMHByaW50fGVufDB8fHx8MTc4NTc3NDMyNXww&ixlib=rb-4.1.0&q=85",
      "https://images.unsplash.com/photo-1586075010923-2dd4570fb338?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1524749292158-7540c2494485?w=1200&q=80&auto=format&fit=crop",
    ],
    bird: BIRDS.bird_mark,
  },
  {
    key: "marketing",
    label: "Marketing Collateral",
    subtitle: "Brochures, reports, banners.",
    tags: [
      "Annual Reports",
      "Brochures",
      "Booklets",
      "Catalogs",
      "Posters",
      "Calendars",
      "Tent Cards",
      "Standees",
    ],
    images: [
      "https://images.unsplash.com/photo-1503694978374-8a2fa686963a?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=1200&q=80&auto=format&fit=crop",
    ],
    bird: BIRDS.bird_pop,
  },
  {
    key: "outdoor",
    label: "Large Format / Outdoor",
    subtitle: "From building to fleet.",
    tags: [
      "Canopy Tents",
      "Vehicle Wrapping",
      "Glass Film",
      "Digital Posters",
      "Frontlit / Backlit Flex",
      "Building Wraps",
      "One-Way Vision",
      "LED Sign Boards",
    ],
    images: [
      "https://images.unsplash.com/photo-1693031630369-bd429a57f115?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxsYXJnZSUyMGZvcm1hdCUyMHByaW50ZXIlMjBtYWNoaW5lJTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3ODU3NzQzMjV8MA&ixlib=rb-4.1.0&q=85",
      "https://images.unsplash.com/photo-1587614382346-4ec70e388b28?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1493612276216-ee3925520721?w=1200&q=80&auto=format&fit=crop",
    ],
    bird: BIRDS.bird_soar,
  },
  {
    key: "packaging",
    label: "Premium Packaging",
    subtitle: "Boxes with a signature.",
    tags: [
      "Custom Packaging",
      "Branded Boxes",
      "Paper Bags",
      "Labels & Decals",
      "Foiling / Emboss",
      "Die-Cut Sleeves",
      "Rigid Boxes",
      "Custom Tape",
    ],
    images: [
      "https://images.unsplash.com/photo-1607083206968-13611e3d76db?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1607166452427-7e4477079cb9?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1553413077-190dd305871c?w=1200&q=80&auto=format&fit=crop",
    ],
    bird: BIRDS.bird_pair,
  },
];

export default function Catalog() {
  const { t } = useLang();
  const localizedTabs = TABS.map((tab) => ({
    ...tab,
    label: t(`catalog.tab_${tab.key}_label`),
    subtitle: t(`catalog.tab_${tab.key}_sub`),
  }));
  const [active, setActive] = useState(TABS[0].key);
  const current = localizedTabs.find((x) => x.key === active);

  return (
    <section
      id="catalog"
      data-testid="catalog-section"
      className="relative bg-[#F4F4F0] text-black border-t border-black"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-36">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-8">
              <span className="w-10 h-px bg-black" />
              <span className="font-mono tracking-[0.22em] uppercase">
                {t("catalog.chapter")}
              </span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-tight">
              {t("catalog.title_1")}{" "}
              <em className="italic">{t("catalog.title_2_italic")}</em>{" "}
              {t("catalog.title_3")} <br />
              {t("catalog.title_4")}{" "}
              <em className="italic">{t("catalog.title_5_italic")}</em>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-base md:text-lg text-black/80 leading-relaxed max-w-[52ch]">
              {t("catalog.sub")}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-6 md:gap-10 border-b border-black pb-4">
          {localizedTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActive(tab.key)}
              data-testid={`catalog-tab-${tab.key}`}
              data-state={active === tab.key ? "active" : "inactive"}
              className="tab-trigger group text-left"
            >
              <div
                className={`font-sans text-xs font-semibold tracking-[0.14em] uppercase mb-2 transition-colors ${
                  active === tab.key ? "text-black" : "text-black/55"
                }`}
              >
                {tab.subtitle}
              </div>
              <div
                className={`font-display text-2xl md:text-4xl leading-tight tracking-tight transition-colors ${
                  active === tab.key ? "text-black" : "text-black/45"
                }`}
              >
                {tab.label}
              </div>
              <span className="tab-bar mt-3" />
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={current.key}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8"
          >
            <div className="md:col-span-7 grid grid-cols-6 grid-rows-6 gap-4 h-[560px] md:h-[620px]">
              <div className="col-span-4 row-span-6 relative overflow-hidden border border-black bg-black">
                <img
                  src={current.images[0]}
                  alt={`${current.label} sample`}
                  className="w-full h-full object-cover"
                  style={current.key === "stationery" ? { transform: "scaleX(-1)" } : undefined}
                />
                <div className="absolute inset-x-0 bottom-0 p-4 md:p-6 bg-gradient-to-t from-black/80 to-transparent text-[#F4F4F0]">
                  <div className="font-mono tracking-[0.2em] uppercase opacity-90">
                    {t("catalog.spotlight")}
                  </div>
                  <div className="font-display text-lg md:text-2xl">
                    {current.label}
                  </div>
                </div>
              </div>
              <div className="col-span-2 row-span-3 relative overflow-hidden border border-black bg-black">
                <img
                  src={current.images[1]}
                  alt={`${current.label} detail`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="col-span-2 row-span-3 relative overflow-hidden border border-black bg-[#F4F4F0] flex items-center justify-center">
                <img
                  src={current.bird}
                  alt=""
                  aria-hidden
                  className="w-2/3 h-2/3 object-contain"
                />
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="border-t border-black pt-4">
                <div className="font-mono tracking-[0.2em] uppercase text-black/65">
                  {t("catalog.shelf")}
                </div>
                <ul className="mt-4">
                  {current.tags.map((tag, i) => (
                    <motion.li
                      key={tag}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className="group border-b border-black/70 py-4 flex items-baseline justify-between"
                      data-testid={`catalog-tag-${current.key}-${i}`}
                    >
                      <span className="font-mono tracking-[0.2em] uppercase text-black/55">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 mx-4 font-display text-2xl md:text-3xl tracking-tight group-hover:italic transition-all">
                        {tag}
                      </span>
                      <span className="font-mono tracking-[0.2em] uppercase text-black/55">
                        {current.key === "outdoor"
                          ? "L-FMT"
                          : current.key === "packaging"
                          ? "PKG"
                          : current.key === "marketing"
                          ? "MKTG"
                          : "STAT"}
                      </span>
                    </motion.li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className="btn-offset mt-8"
                  data-testid={`catalog-cta-${current.key}`}
                >
                  {t("catalog.cta")} {current.label}
                </a>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
